// @flow
/* eslint-disable import/prefer-default-export */
import { createSelector } from 'reselect';
import idx from 'idx';

import * as cc from 'common/constants';
import { WhiteLabel } from 'settings';
import * as advertiserSelectors from 'modules/advertiser/selectors';
import type { AppState } from 'store/types';

import * as c from './constants';
import type { RootState } from './reducers';

const rootModule = (state: AppState): RootState => state[c.MODULE_NAME];

// READY
// ============================================================================

export const authIsReady = createSelector([rootModule], (state) => state.ready);

// ADVERTISER
// TODO: need to move this selectors to "modules/advertiser"
// ============================================================================

export const sofarmAdvertiser = advertiserSelectors.sofarmOwnAdvertiser;
export const billingAdvertiser = advertiserSelectors.billingOwnAdvertiser;

export const authUserAdvertiserId = createSelector(
  [sofarmAdvertiser],
  (advertiser) => (advertiser ? advertiser.id : undefined),
);

export const authUserBalance = createSelector([billingAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.balance.balance),
);
export const authUserAvailableBalance = createSelector([billingAdvertiser], (advertiser) =>
  idx(advertiser, (_) => _.balance.availableBalance),
);

export const authUserBillingInfo = createSelector(
  [billingAdvertiser],
  (advertiser) => (advertiser ? advertiser.billingInfo : undefined),
);

export const authUserVoucher = createSelector(
  [billingAdvertiser],
  (advertiser) => (advertiser ? advertiser.currentVoucher : undefined),
);

// TOKEN
// ============================================================================

export const authToken = createSelector([rootModule], (state) => idx(state, (_) => _.token));

export const authId = createSelector([authToken], (token) => idx(token, (_) => _.authId));
export const accessToken = createSelector([authToken], (token) => idx(token, (_) => _.accessToken));
export const refreshToken = createSelector([authToken], (token) =>
  idx(token, (_) => _.refreshToken),
);
export const authUserScopes = createSelector([authToken], (token) => idx(token, (_) => _.scopes));
export const isRefreshing = createSelector(
  [authToken],
  (token) => idx(token, (_) => _.refreshing) || false,
);
export const authError = createSelector([authToken], (token) => idx(token, (_) => _.error));

export const hasAuthScope = (scope: string) =>
  createSelector([authUserScopes], (scopes) => (scopes ? scopes.includes(scope) : false));

// USER
// ============================================================================

export const sofarmUser = createSelector([rootModule], (state) =>
  idx(state, (_) => _.user[cc.APP.SOFARM]),
);
export const sfpagesUser = createSelector([rootModule], (state) =>
  idx(state, (_) => _.user[cc.APP.SFPAGES]),
);
export const billingUser = createSelector([rootModule], (state) =>
  idx(state, (_) => _.user[cc.APP.BILLING]),
);

export const hasAuthUser = (app: cc.AppName) =>
  createSelector([rootModule], (state) => idx(state, (_) => _.user[app]) != null);

export const isAuthenticated = createSelector(
  [rootModule],
  (state) => state.token != null && !!state.token.accessToken,
);

export const authUserId = createSelector([sofarmUser], (user) => idx(user, (_) => _.id));
export const authUserHash = createSelector([sofarmUser], (user) => idx(user, (_) => _.hash));
export const authUserEmail = createSelector([sofarmUser], (user) => idx(user, (_) => _.email));
export const authUserName = createSelector([sofarmUser], (user) => idx(user, (_) => _.name));
export const authUserCreatedAt = createSelector([sofarmUser], (user) =>
  idx(user, (_) => _.createdAt),
);
export const authUserServerTimeOffset = createSelector([sofarmUser], (user) =>
  idx(user, (_) => _.serverTimeOffset),
);
export const isAuthUserVip = createSelector(
  [sofarmUser],
  (user) => idx(user, (_) => _.isVip) || false,
);

export const voucherTypesAvailableToGenerate = createSelector([billingUser], (user) =>
  idx(user, (_) => _.voucherTypesAvailableToGenerate),
);
export const resellerVoucherTypesAvailableToGenerate = createSelector(
  [voucherTypesAvailableToGenerate],
  (vouchers) => (Array.isArray(vouchers) ? vouchers.filter((i) => i.percentReferral != null) : []),
);

// PERMISSIONS
// ============================================================================

export const sofarmPermissions = createSelector(
  [sofarmUser],
  (user) => (user ? user.permissions : []),
);
export const sfpagesPermissions = createSelector(
  [sfpagesUser],
  (user) => (user ? user.permissions : []),
);
export const billingPermissions = createSelector(
  [billingUser],
  (user) => (user ? user.permissions : []),
);

export const permissions = createSelector(
  [
    isAuthenticated,
    createSelector(
      [sofarmPermissions, sfpagesPermissions, billingPermissions],
      (sofarm, sfpages, billing) => [...(sofarm || []), ...(sfpages || []), ...(billing || [])],
    ),
  ],
  (authed, perms) => (authed ? perms : [cc.PERMISSIONS.GUEST_ACCESS]),
);

export const authHasPermission = (givenPermissions: string | string[]) =>
  createSelector(
    [permissions],
    (userPermissions): boolean => {
      const givenPermissionsArray = [].concat(givenPermissions);

      if (!givenPermissions || !givenPermissionsArray.length) {
        return true;
      }

      return givenPermissionsArray.reduce((acc, i) => acc || userPermissions.includes(i), false);
    },
  );

export const isBlackHatMode = authHasPermission(cc.PERMISSIONS.BLACKHAT_INTERFACE);
export const canChooseOnlyManagedAdvertisers = hasAuthScope(cc.SCOPE.KAM);

export const canBuyProfiles = (state: AppState) =>
  authHasPermission(cc.PERMISSIONS.BUY_PROFILE)(state) && !WhiteLabel.get('hideBuyProfiles');
export const canRenewProfile = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.RENEW_PROFILE),
    authHasPermission(cc.PERMISSIONS.RENEW_OWN_PROFILE),
    authHasPermission(cc.PERMISSIONS.RENEW_MANAGED_PROFILE),
  ],
  (...perms) => perms.some(Boolean),
);
export const canSeeAdvertiserInRenewProfile = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.RENEW_PROFILE),
    authHasPermission(cc.PERMISSIONS.RENEW_MANAGED_PROFILE),
  ],
  (...perms) => perms.some(Boolean),
);

export const canSellProfiles = authHasPermission(cc.PERMISSIONS.SALE_PROFILE);
export const canSeeSaleProfileFields = authHasPermission(cc.PERMISSIONS.SEE_SALE_PROFILE_FIELDS);
export const canStartExpiredProfile = authHasPermission(cc.PERMISSIONS.START_EXPIRED_PROFILE);

// Payment Sources
export const canSeePaymentMainFieldsBH = authHasPermission(
  cc.PERMISSIONS.BLACKHAT_SHOW_MAIN_PAYMENT_SOURCE_FIELDS,
);
export const canSeePaymentInternalFields = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.SHOW_PAYMENTS_INTERNAL_FIELD),
    authHasPermission(cc.PERMISSIONS.SHOW_PAYMENTS_INTERNAL_FIELD_OWN),
  ],
  (...perms) => perms.some(Boolean),
);
export const canEditPaymentLinks = authHasPermission(cc.PERMISSIONS.MANAGE_PAYMENTS);
export const canManagePayments = authHasPermission(cc.PERMISSIONS.MANAGE_PAYMENTS);
export const canEditPayments = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.EDIT_PAYMENT_SOURCE),
    authHasPermission(cc.PERMISSIONS.EDIT_PAYMENT_SOURCE_OWN),
  ],
  (...perms) => perms.some(Boolean),
);
export const canBuyPayments = (state: AppState) =>
  authHasPermission(cc.PERMISSIONS.SELL_CARD_TO_OWN_ADVERTISER)(state) &&
  authHasPermission(cc.PERMISSIONS.ACCESS_ADVERTISERS)(state);
export const canSellPayments = authHasPermission(cc.PERMISSIONS.SELL_CARD_TO_ADVERTISER);
export const canTopUpOwnPayments = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.TOP_UP_OWN_PAYMENTS),
    authHasPermission(cc.PERMISSIONS.ACCESS_ADVERTISERS),
    canSellPayments,
  ],
  (canTopUp, haveAdvertiserAccess, canSell) => (canTopUp && haveAdvertiserAccess) || canSell,
);
export const canWithdrawOwnPayments = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.WITHDRAW_OWN_PAYMENTS),
    authHasPermission(cc.PERMISSIONS.ACCESS_ADVERTISERS),
    canSellPayments,
  ],
  (canWithdraw, haveAdvertiserAccess, canSell) => (canWithdraw && haveAdvertiserAccess) || canSell,
);
export const canDirectNoctisOneOperations = authHasPermission(cc.PERMISSIONS.NOCTIS_ONE_OPERATIONS);

// Domain
export const canRenewDomain = authHasPermission(cc.PERMISSIONS.SAFEPAGES_RENEW_DOMAIN);
export const canSeeOwnerDomain = authHasPermission(cc.PERMISSIONS.SAFEPAGES_SHOW_OWNER_DOMAIN);
export const canSellDomain = authHasPermission(cc.PERMISSIONS.SAFEPAGES_SELL_DOMAIN);
export const canSeeBlackhatDomain = authHasPermission(cc.PERMISSIONS.SAFEPAGES_BLACKHAT_DOMAIN);
export const canSeeDomainOwner = authHasPermission(cc.PERMISSIONS.SAFEPAGES_SHOW_OWNER_DOMAIN);

// Cloaker
export const canBuyCloaker = authHasPermission(cc.PERMISSIONS.BLACKHAT_INTERFACE);
export const canUseCombinedCloaker = authHasPermission(
  cc.PERMISSIONS.SAFEPAGES_COMBINED_CLOAKER_USE,
);
export const canSellCloaker = authHasPermission(cc.PERMISSIONS.SAFEPAGES_SELL_CLOAKER);

export const canGenerateResellerVoucher = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.ACCESS_ADVERTISERS),
    authHasPermission(cc.PERMISSIONS.BLACKHAT_INTERFACE),
    resellerVoucherTypesAvailableToGenerate,
  ],
  (hasAccessAdvertiser, hasBlackHatInterface, voucherTypes) =>
    hasAccessAdvertiser && hasBlackHatInterface && voucherTypes.length > 0,
);
export const canSeeAdvertiser = authHasPermission(cc.PERMISSIONS.SHOW_ADVERTISER);
export const canSeeAnyAdvertiserVoucherList = authHasPermission(
  cc.PERMISSIONS.SHOW_ADVERTISER_VOUCHER_LIST,
);
export const canSeeMyAdvertiserVoucherList = canGenerateResellerVoucher;
export const canSeeAdvertiserVoucherList = () => true;
createSelector(
  [canSeeAnyAdvertiserVoucherList, canSeeMyAdvertiserVoucherList],
  (canSeeAny, canSeeMy) => canSeeAny || canSeeMy,
);
export const canTopUpWithInvoiceForSelf = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.BILLING_CREATE_TOPUP_INVOICE),
    authHasPermission(cc.PERMISSIONS.BILLING_CREATE_TOPUP_INVOICE_OWN),
  ],
  (...perms) => perms.some(Boolean),
);
export const canTopUpWithInvoice = authHasPermission(cc.PERMISSIONS.BILLING_CREATE_TOPUP_INVOICE);

// Profile Sessions

export const canSeeProfileSession = createSelector(
  [
    authHasPermission(cc.PERMISSIONS.SHOW_PROFILE_SESSION),
    authHasPermission(cc.PERMISSIONS.SHOW_MANAGED_PROFILE_SESSION),
  ],
  (...perms) => perms.some(Boolean),
);



// WEBPACK FOOTER //
// ./src/modules/auth/selectors.js